import fdaLogo from '@/assets/fda.png';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { Database, FileText } from 'lucide-react';
import { useState, type FC, type ReactNode } from 'react';
// Props for file/document citations (with quote)
interface FileCitationProps {
  type: 'file';
  sourceName: string;
  quotedText: string;
  page?: number;
  children: ReactNode;
  groupUname?: string;
}

// Props for database citations (optional quote, has external link)
interface DatabaseCitationProps {
  type: 'database';
  sourceName: string;
  databaseUrl: string;
  quotedText?: string; // Optional - only if there's actual quotable text
  children: ReactNode;
  toolName: string;
}

export type CitationHoverModalProps = FileCitationProps | DatabaseCitationProps;

export const CitationHoverModal: FC<CitationHoverModalProps> = props => {
  const [isOpen, setIsOpen] = useState(false);

  const isFdaDatabaseSource =
    props.type === 'database' &&
    [
      'fda510kSubmissionDbSearch',
      'fdaDeviceClassificationDbSearch',
      'fdaAdverseEventsDbSearch',
    ].includes(props.toolName);

  const isFdaFileSource =
    props.type === 'file' &&
    props.groupUname &&
    ['fda-guidance-radio', 'fdaguidance', 'fda21cfr'].includes(
      props.groupUname
    );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span
          className="cursor-pointer transition-colors inline-block"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          style={{ padding: 0, margin: 0 }}
        >
          {props.children}
        </span>
      </PopoverTrigger>
      <PopoverContent className="max-w-[500px] w-fit px-4 shadow-lg border border-neutral-200 bg-white">
        <FlexCol gap="3">
          {/* Header */}
          <FlexRow
            justifyContent="between"
            alignItems="center"
            gap="2"
            className="border-b border-neutral-200 pb-3 px-2 w-full"
          >
            {props.type === 'file' ? (
              isFdaFileSource ? (
                <img
                  src={fdaLogo}
                  alt="FDA Logo"
                  className="w-8 h-8 opacity-50"
                />
              ) : (
                <FileText className="w-4 h-4 text-neutral-900" />
              )
            ) : isFdaDatabaseSource ? (
              <img
                src={fdaLogo}
                alt="FDA Logo"
                className="w-8 h-8 opacity-50"
              />
            ) : (
              <Database className="w-4 h-4 text-neutral-900" />
            )}
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-bold" className="text-neutral-900 flex-1">
                {props.sourceName}
              </Text>
            </Ellipsis>

            {props.type === 'file' && props.page && (
              <Text
                variant="body-sm"
                className="text-neutral-600 flex-shrink-0"
              >
                Page {props.page}
              </Text>
            )}
          </FlexRow>

          <FlexCol gap="2">
            {props.type === 'file' ? (
              // File citation: show the quote
              <Text
                variant="body-sm"
                className="text-neutral-800 leading-relaxed italic"
                as="div"
              >
                "{props.quotedText}"
              </Text>
            ) : (
              // Database citation: show quote if available, plus link to source
              <FlexCol gap="2">
                {props.quotedText && (
                  <Text
                    variant="body-sm"
                    className="text-neutral-800 leading-relaxed italic"
                    as="div"
                  >
                    "{props.quotedText}"
                  </Text>
                )}
                <Text variant="body-sm" className="text-neutral-600">
                  Data retrieved from official FDA database.
                </Text>
              </FlexCol>
            )}
          </FlexCol>
        </FlexCol>
      </PopoverContent>
    </Popover>
  );
};
